import { graphql, useFragment } from "react-relay";
import type { postPreviewFragment$key } from "./__generated__/postPreviewFragment.graphql";
import { Card } from "@/components/ui/card";
import { PostBottomActions } from "./post-bottom-actions";
import { PostHeader } from "./post-header";
import { PostContentPreview } from "./post-content-preview";
import { PostCover } from "./post-cover";
import { Link } from "@tanstack/react-router";
import { format, isAfter } from "date-fns";
import { Icon } from "@/components/icon";
import { cn } from "@/utils";
import { PostAttachmentsPreview } from "./post-attachments-preview";

export function PostPreview({
	post,
	showUniversalFeedPin,
	showSpaceFeedPin,
	onUpdate,
}: {
	post: postPreviewFragment$key;
	showUniversalFeedPin?: boolean;
	showSpaceFeedPin?: boolean;
	onUpdate?: () => void;
}) {
	const data = useFragment<postPreviewFragment$key>(
		graphql`
    fragment postPreviewFragment on Post @argumentDefinitions(club: { type: "String!" }) {
      title
      slug
      space {
        slug
      }
      scheduledPublishDate
      ...postBottomActionsFragment
      ...postHeaderFragment @arguments(club: $club)
      ...postContentPreviewFragment
      ...postCoverFragment
      ...postAttachmentsPreview_post
    }
  `,
		post,
	);

	if (!data) {
		return null;
	}

	const showScheduled =
		data.scheduledPublishDate !== null &&
		isAfter(new Date(data.scheduledPublishDate), new Date());

	return (
		<div className={showScheduled ? "border rounded-xl bg-muted" : undefined}>
			{showScheduled && (
				<div className="px-3 py-2 rounded-t-xl bg-muted text-sm flex flex-row items-center font-mono justify-start gap-1.5 font-normal text-muted-foreground">
					<Icon name="Clock10" className="size-3" />
					Agendado para{" "}
					{format(new Date(data.scheduledPublishDate), "dd/MM/yyyy HH:mm")}
				</div>
			)}

			<Card
				className={cn(
					"p-0 prose dark:prose-invert w-full min-w-full overflow-hidden",
					showScheduled && "border-0 border-t",
				)}
			>
				<PostCover post={data} />

				<div className="px-6 pb-6 first:pt-6 flex flex-col gap-6">
					<PostHeader
						post={data}
						showUniversalFeedPin={showUniversalFeedPin}
						showSpaceFeedPin={showSpaceFeedPin}
						onUpdate={onUpdate}
					/>

					<Link
						to="/comunidade/s/$space/posts/$post"
						params={{ space: data.space?.slug!, post: data.slug }}
						className="font-semibold text-2xl/6 sm:text-xl/6 no-underline"
					>
						{data.title}
					</Link>

					<PostContentPreview post={data} />

					<PostAttachmentsPreview post={data} />

					<PostBottomActions post={data} />
				</div>
			</Card>
		</div>
	);
}
